import { useRef, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import Layout from './Layout';
import MetaTags from './meta-tags';
import apiService from '../services/api-service';
import { translateCollectionName, getCollectionTypeLabel } from '../constants';
import { useLanguage } from '../contexts/language-context';
import { SPINNER_CLASSES } from '../styles';
import useUnifiedFilters from '../hooks/use-unified-filters';
import MediaFilters from './media-filters';
import { MediaCard, MediaListItem } from './media-display';
import { MOVIE_GENRES, TV_GENRES, MOVIE_SORT_OPTIONS, TV_SORT_OPTIONS, ANIMATION_DELAYS } from '../constants';
import { createStableKey } from '../utils/deduplication';
import { getShowingText } from '../utils/lithuanian-grammar';
import { useScrollRestoration } from '../hooks/use-scroll-restoration';

function CollectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const [viewMode, setViewMode] = useState('grid');

  // Use unified filters for collection items (separate from movie/tv pages)
  const {
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilters
  } = useUnifiedFilters({}, 'collection');

  // Fetch list items (includes list_info metadata)
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error
  } = useInfiniteQuery({
    queryKey: ['collection-items', id, language, filters.availableLT],
    queryFn: async ({ pageParam = 1 }) => {
      const params = { 
        page: pageParam, 
        limit: 50, 
        language 
      };
      
      // Add Lithuania availability filter if active
      if (filters.availableLT === 'true' || filters.availableLT === true) {
        params.availableLT = 'true';
      }
      
      const response = await apiService.getListById(id, params);
      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      if (lastPage.has_more === false) return undefined;
      if (!lastPage.results?.length) return undefined;
      return currentPage + 1;
    },
    initialPageParam: 1,
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Extract collection metadata from list_info in the first page response
  const collection = useMemo(() => {
    const firstPage = data?.pages?.[0];
    const listInfo = firstPage?.list_info;
    if (!listInfo) return null;
    
    return {
      id: listInfo.list_id || id,
      name: listInfo.name || `Collection ${id}`,
      user: listInfo.user || 'Unknown',
      likes: listInfo.likes || 0,
      type: listInfo.type || 'mixed',
      itemCount: listInfo.total_items || 0,
      description: listInfo.description || ''
    };
  }, [data?.pages, id]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rawItems = useMemo(() => {
    return data?.pages?.flatMap(page => page.results || []) || [];
  }, [data?.pages]);
  
  // Apply client-side filtering
  const items = useMemo(() => {
    if (!rawItems.length) return rawItems;
    
    return rawItems.filter(item => {
      // Filter out items without poster image or description
      if (!item.poster_path) return false;
      if (!item.overview || item.overview.trim() === '') return false;
      
      // IMDB Rating filter
      if (filters.imdbRating > 0) {
        const imdbRating = item.enhanced_ratings?.real_ratings?.imdb?.rating || 
                          (item.ratings?.find(r => r.source === 'imdb')?.value / 10) || 
                          item.score / 10 || 0;
        if (imdbRating < filters.imdbRating) return false;
      }
      
      // Rotten Tomatoes Rating filter
      if (filters.rottenTomatoesRating > 0) {
        const rtRating = item.enhanced_ratings?.real_ratings?.rottenTomatoes?.rating || 
                        item.ratings?.find(r => r.source === 'tomatoes')?.value || 
                        item.score || 0;
        if (rtRating < filters.rottenTomatoesRating) return false;
      }
      
      // Year filter
      const releaseYear = parseInt(item.release_date?.substring(0, 4) || item.first_air_date?.substring(0, 4) || item.release_year || '0');
      if (filters.fromYear && releaseYear < parseInt(filters.fromYear)) return false;
      if (filters.toYear && releaseYear > parseInt(filters.toYear)) return false;
      
      // Genre filter
      if (filters.selectedGenre && filters.selectedGenre !== 'all') {
        const genreId = parseInt(filters.selectedGenre);
        const itemGenres = item.genre_ids || item.genres?.map(g => g.id) || [];
        if (!itemGenres.includes(genreId)) return false;
      }
      
      return true;
    });
  }, [rawItems, filters.imdbRating, filters.rottenTomatoesRating, filters.fromYear, filters.toYear, filters.selectedGenre]);

  // Try to get list metadata from first page response
  const listMeta = data?.pages?.[0]?.list_metadata || data?.pages?.[0]?.metadata || null;
  
  // Derive collection info from metadata if not found in collections list
  const collectionInfo = collection || (listMeta ? {
    id: id,
    name: listMeta.name || listMeta.title || `Collection ${id}`,
    type: listMeta.mediatype === 'show' ? 'show' : 'movie',
    user: listMeta.user_name || listMeta.username || 'Unknown',
    itemCount: listMeta.items || listMeta.item_count || 0,
    likes: listMeta.likes || 0
  } : null);

  // Determine media type for filters and cards
  const mediaType = collectionInfo?.type === 'show' ? 'tv' : 'movie';
  const genres = mediaType === 'tv' ? TV_GENRES : MOVIE_GENRES;
  const sortOptions = mediaType === 'tv' ? TV_SORT_OPTIONS : MOVIE_SORT_OPTIONS;

  // Scroll restoration for navigating back from detail pages
  const locationKey = useScrollRestoration({
    mediaType: `collection-${id}`,
    searchQuery: '',
    filters,
    isLoading,
    itemsLength: items.length
  });

  // Loading state
  if (isLoading && !rawItems.length) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className={SPINNER_CLASSES.XLARGE_PRIMARY}></div>
        </div>
      </Layout>
    );
  }

  // Error state - collection not found
  if (isError && !rawItems.length) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <svg className="w-20 h-20 text-gray-600 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-2xl font-bold text-white mb-4">{t('collectionNotFound') || 'Kolekcija nerasta'}</h1>
          <p className="text-gray-400 mb-8">{error?.message || 'Tokios kolekcijos nėra arba ji buvo pašalinta.'}</p>
          <button
            onClick={() => navigate('/collections')}
            className="btn-primary px-6 py-3"
          >
            {t('backToCollections') || 'Grįžti į kolekcijas'}
          </button>
        </div>
      </Layout>
    );
  }

  const typeLabel = collectionInfo?.type === 'movie' 
    ? t('movies') 
    : collectionInfo?.type === 'show' 
    ? t('tvShows') 
    : getCollectionTypeLabel(collectionInfo?.type || 'mixed', language);
  
  const totalCount = data?.pages?.[0]?.total_results || collectionInfo?.itemCount || rawItems.length;
  const collectionName = translateCollectionName(collectionInfo?.name || `Collection ${id}`, language);

  return (
    <Layout resetFilters={resetFilters}>
      <MetaTags
        title={`${collectionInfo?.name || 'Collection'} - FilmųDB`}
        description={`${collectionInfo?.name || 'Collection'} - ${totalCount} ${typeLabel}. Kuratorius: ${collectionInfo?.user || 'Unknown'}.`}
        url={`https://filmudb.lt/collections/${id}`}
      />

      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        {/* Header */}
        <section className="relative py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <button
            onClick={() => navigate('/collections')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('backToCollections') || 'Grįžti į kolekcijas'}
          </button>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              {/* Type Badge */}
              <span className={`inline-block mb-3 text-xs font-medium px-2.5 py-1 rounded-full ${
                collectionInfo?.type === 'movie' 
                  ? 'bg-primary-600 text-white' 
                  : collectionInfo?.type === 'show'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-600 text-white'
              }`}>
                {typeLabel}
              </span>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
                {collectionName}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-gray-400">
                {collectionInfo?.user && (
                  <>
                    <span>{t('curator') || 'Kuratorius'}: <span className="text-gray-300">{collectionInfo.user}</span></span>
                    <span className="hidden sm:inline">•</span>
                  </>
                )}
                <span>{totalCount.toLocaleString()} {t('items') || 'elementų'}</span>
                {collectionInfo?.likes > 0 && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                      </svg>
                      {collectionInfo.likes}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <MediaFilters
        mediaType={mediaType}
        title=""
        subtitle={hasActiveFilters ? `Filtruota: rodoma ${items.length} iš ${totalCount}` : ''}
        filters={filters}
        onFiltersChange={updateFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        genres={genres}
        sortOptions={sortOptions}
        hideTitle={true}
      />

      {/* Content Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {items.length === 0 && !isLoading ? (
          <div className="text-center py-16">
            {hasActiveFilters ? (
              <>
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-400 mb-2">Nerasta rezultatų su šiais filtrais</h3>
                <p className="text-gray-500 mb-4">Pabandykite sumažinti filtravimo kriterijus</p>
                <button
                  onClick={resetFilters}
                  className="btn-secondary px-4 py-2"
                >
                  Išvalyti filtrus
                </button>
              </>
            ) : (
              <>
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v1a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1h4zm0 0v16a1 1 0 001 1h8a1 1 0 001-1V4" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-400 mb-2">{t('collectionEmpty') || 'Kolekcija tuščia'}</h3>
                <p className="text-gray-500">{t('collectionEmptyDesc') || 'Šioje kolekcijoje dar nėra elementų.'}</p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="mb-6 text-gray-400 text-sm">
              {getShowingText(items.length, 'item')}
              {hasActiveFilters && ` (filtruota)`}
            </div>

            {viewMode === 'grid' ? (
              <div className="grid-uniform">
                {items.map((item, index) => (
                  <div 
                    key={createStableKey(item, mediaType, index)} 
                    className="animate-slide-up h-full" 
                    style={{ animationDelay: `${(index % 20) * ANIMATION_DELAYS.CARD_STAGGER}ms` }}
                  >
                    <MediaCard 
                      media={item} 
                      mediaType={mediaType}
                      filters={filters}
                      locationKey={locationKey}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="list-uniform">
                {items.map((item, index) => (
                  <div 
                    key={createStableKey(item, mediaType, index)} 
                    className="animate-slide-up" 
                    style={{ animationDelay: `${(index % 20) * ANIMATION_DELAYS.LIST_ITEM_STAGGER}ms` }}
                  >
                    <MediaListItem 
                      media={item} 
                      mediaType={mediaType}
                      filters={filters}
                      locationKey={locationKey}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Load More Trigger */}
            {hasNextPage && (
              <div
                ref={loadMoreRef}
                className="flex justify-center items-center py-8"
              >
                {isFetchingNextPage ? (
                  <div className={SPINNER_CLASSES.MEDIUM_PRIMARY}></div>
                ) : (
                  <span className="text-gray-500 text-sm">{t('loading') || 'Kraunama...'}</span>
                )}
              </div>
            )}
          </>
        )}
      </main>
      </div>
    </Layout>
  );
}

export default CollectionDetail;
